/**
 * Log View
 */

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
import moment from 'moment';
import 'moment-timezone';
import { isMobile } from 'react-device-detect';

import { hourLabels, MILLISECONDS_IN_DAY } from './utils';
import { TIME_MILLISECONDS_FORMAT } from 'src/utils/dateUtils';

import './logView.scss';

export class LogView extends React.Component {
  constructor() {
    super();

    this.state = {
      violationBlocks: [],
      rawViolationBlocks: [],
      activeViolationId: -1,
      displayInsertBlocks: [],
    };
  }

  /**
   * Lifecycle
   */

  componentDidUpdate(prevProps) {
    if (
      prevProps.violations !== this.props.violations ||
      prevProps.timeZone !== this.props.timeZone ||
      prevProps.date !== this.props.date
    ) {
      this.props.violations &&
        this.props.timeZone &&
        this.calculateViolationBlocks(this.props.violations);
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { editBlocks, activeViolationId } = nextProps;
    if (
      nextProps.activeViolationId !== prevState.activeViolationId ||
      nextProps.editBlocks !== prevState.editBlocks
    ) {
      return {
        activeViolationId,
        editBlocks,
      };
    }
    return null;
  }

  calculateMergedViolations(violations) {
    const violationBlocks = [];

    let cLowerBound = 0;
    let cUpperBound = 0;
    for (let i = 0; i < violations.length; i++) {
      const lowerBound = violations[i].startTime;
      const upperBound = violations[i].endTime ? violations[i].endTime : Date.now();

      if (i === 0) {
        cLowerBound = lowerBound;
        cUpperBound = upperBound;

        if (violations.length === 1) {
          violationBlocks.push({
            startTime: lowerBound,
            endTime: upperBound,
          });
          break;
        }
      } else {
        if (lowerBound > cUpperBound) {
          violationBlocks.push({
            startTime: cLowerBound,
            endTime: cUpperBound,
          });
          cLowerBound = lowerBound;
          cUpperBound = upperBound;
        } else if (upperBound >= cUpperBound && i + 1 < violations.length) {
          cUpperBound = upperBound;
        } else if (i + 1 === violations.length) {
          violationBlocks.push({
            startTime: cLowerBound,
            endTime: cUpperBound,
          });
        }
      }
    }

    return violationBlocks;
  }

  convertTimestampsToWidths(violationBlocks) {
    const blocks = [];
    const startOfDay = this.props.date.valueOf();

    for (let i = 0; i < violationBlocks.length; i++) {
      const startTs = violationBlocks[i].startTime;
      let endTs = violationBlocks[i].endTime;
      if (!endTs) endTs = moment.tz(moment(), this.props.timeZone);

      const startTime = moment.tz(startTs, this.props.timeZone);

      const endTime = moment.tz(endTs, this.props.timeZone);

      let startMs = moment.duration(startTime.format(TIME_MILLISECONDS_FORMAT)).asMilliseconds();

      let endMs = moment.duration(endTime.format(TIME_MILLISECONDS_FORMAT)).asMilliseconds();

      let offset = 0;
      let width = 0;

      if (startTs > startOfDay) {
        offset = (startMs / MILLISECONDS_IN_DAY) * 100;
      } else {
        startMs = 0;
      }

      if (endTime.isAfter(startTime, 'day') && !this.props.date.isSame(endTime, 'day')) {
        endMs = MILLISECONDS_IN_DAY;
      }
      width = ((endMs - startMs) / MILLISECONDS_IN_DAY) * 100;

      blocks.push({
        offset,
        width,
        violationId: violationBlocks[i].violationId,
      });
    }

    return blocks;
  }

  calculateViolationBlocks(violations) {
    const sortedViolations = violations.slice().sort((a, b) => {
      return a.startTime - b.startTime;
    });
    let violationBlocks = this.calculateMergedViolations(sortedViolations);
    violationBlocks = this.convertTimestampsToWidths(violationBlocks);

    const rawViolationBlocks = this.convertTimestampsToWidths(violations);

    this.setState({
      violationBlocks,
      rawViolationBlocks,
    });
  }

  /**
   * Render
   */

  render() {
    const statusHtml =
      this.props.statusGrid &&
      this.props.statusGrid.map((status, i) => {
        const style = {
          width: status.width + '%',
          left: status.offset + '%',
        };
        const className = 'status ' + status.state;

        return <div key={'state-' + i} className={className} style={style} />;
      });

    const violationBlocks = this.state.violationBlocks.map((v, i) => {
      const style = {
        width: v.width + '%',
        left: v.offset + '%',
      };
      let className = 'violation';
      if (this.state.activeViolationId !== -1) {
        className += ' no-show';
      }

      return <div key={'violation-' + i} className={className} style={style} />;
    });

    const rawViolationBlocks = this.state.rawViolationBlocks.map((v, i) => {
      const style = {
        width: v.width + '%',
        left: v.offset + '%',
      };
      let className = 'raw-violation';
      if (this.state.activeViolationId === v.violationId) {
        className += ' show';
      }

      return <div key={'rawViolation-' + i} className={className} style={style} />;
    });

    return (
      <div className="logview-ctn">
        <div className="hour-legend">
          {hourLabels({
            timeZone: this.props.timeZone,
            logStartTimeISO: this.props.logStartTimeISO,
            logLengthHours: this.props.logLengthHours,
            isMobile,
          })?.map((label, i) => (
            <div key={i} className="hour">
              {label}
            </div>
          ))}
        </div>
        <div className="logview-grid-ctn">
          <div className="legend">
            <div className="key">OFF</div>
            <div className="key">SB</div>
            <div className="key">D</div>
            <div className="key">ON</div>
          </div>
          <div className="grid">
            {violationBlocks}
            {rawViolationBlocks}
            <div className="grid-line" />
            <div className="grid-line" />
            <div className="grid-line" />
            <div className="grid-line" />
            {statusHtml}
          </div>
          <div className="hour-totals">
            <div className="title">
              Total <br /> Hours
            </div>
            {this.props.statusTimes && (
              <>
                <div className="time">{this.props.statusTimes.off}</div>
                <div className="time">{this.props.statusTimes.sb}</div>
                <div className="time">{this.props.statusTimes.d}</div>
                <div className="time">{this.props.statusTimes.on}</div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
